<script setup lang="ts">
import { computed, inject, onMounted, ref, shallowRef, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type CrossValidationReportDto, type EstimatorReportDto, getReportName } from "@/dto";
import { raiseErrorKey } from "@/injectionKeys";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { ProjectDetailSections, RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useWorkspaceFetcher } from "@/services/workspaceFetcher";
import CrossValidationReport from "@/views/reports/CrossValidationReport.vue";
import ReportDrawer from "@/views/reports/ReportDrawer.vue";

const api = useApi();
const route = useRoute();

const raiseError = inject(raiseErrorKey);

const isLoading = ref(true);
const reportDrawer = useTemplateRef<InstanceType<typeof ReportDrawer>>("reportDrawer");
const crossValidationReportComponent =
  useTemplateRef<InstanceType<typeof CrossValidationReport>>("crossValidationReport");
const report = shallowRef<CrossValidationReportDto>();
let estimators = new Map<number, EstimatorReportDto>();

const workspacePublicId = computed(() => route.params.workspacePublicId as string);
const projectName = computed(() => route.params.projectName as string);
const reportId = computed(() => route.params.reportId as string);
const reportName = computed(() => (report.value ? getReportName(report.value) : "..."));

const workspace = useWorkspaceFetcher(workspacePublicId.value);

async function fetchReport() {
  isLoading.value = true;
  const r = await api.fetchCrossValidationReport(
    workspacePublicId.value,
    projectName.value,
    parseInt(reportId.value)
  );
  if (r === null) {
    raiseError?.(404);
  } else {
    // fetch sub reports and filter potentially not found
    const estimatorReports = (
      await Promise.all(
        r.estimators.map((id) =>
          api.fetchEstimatorReport(workspacePublicId.value, projectName.value, id)
        )
      )
    ).filter((r) => r !== null);
    estimators = new Map(estimatorReports.map((e) => [e.id, e]));
    report.value = r;
    isLoading.value = false;
  }
}

onMounted(async () => {
  await fetchReport();
});
</script>

<template>
  <DefaultPageLayout
    :breadcrumb-items="[
      {
        label: workspace?.name ?? workspacePublicId,
        to: { name: RouteNames.Projects, params: { workspacePublicId } },
        icon: 'icon-masonry',
      },
      {
        label: projectName,
        to: {
          name: RouteNames.ProjectDetail,
          params: {
            workspacePublicId,
            projectName,
            section: ProjectDetailSections.CrossValidationReports,
          },
        },
        icon: 'icon-punch-card',
        iconColor: 'var(--color-purple-500)',
      },
      {
        label: 'Cross-validations',
        to: {
          name: RouteNames.ProjectDetail,
          params: {
            workspacePublicId,
            projectName,
            section: ProjectDetailSections.CrossValidationReports,
          },
        },
      },
      {
        label: reportName,
      },
    ]"
  >
    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>

      <div v-else-if="report !== undefined" class="cross-validation-report">
        <CrossValidationReport
          ref="crossValidationReport"
          :report="report"
          :estimators="estimators"
          :use-anchor="true"
          @open-estimator="(reportId: number) => reportDrawer?.openEstimatorReport(reportId)"
        >
          <template #summaryPrelude>
            <div class="back">
              <RouterLink
                :to="{
                  name: RouteNames.ProjectDetail,
                  params: {
                    workspacePublicId: workspacePublicId,
                    projectName: projectName,
                    section: ProjectDetailSections.CrossValidationReports,
                  },
                }"
                class="text-base-medium"
              >
                <i class="icon icon-arrow-left" /> Cross-validations
              </RouterLink>
            </div>
          </template>

          <template #contentPrelude>
            <div class="content-prelude text-base-medium">&nbsp;</div>
          </template>
        </CrossValidationReport>
      </div>
    </Transition>
  </DefaultPageLayout>

  <ReportDrawer
    ref="reportDrawer"
    :workspace-public-id="workspacePublicId"
    :project-name="projectName"
  >
    <template #title>
      Estimator report for split #{{ crossValidationReportComponent?.selectedSplitIndex }}
    </template>
  </ReportDrawer>
</template>

<style scoped>
.loading {
  margin-top: 40dvh;
  text-align: center;
}

.cross-validation-report {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;

  .back,
  .content-prelude {
    padding: var(--numbers-16) 0;
  }

  .back {
    color: var(--content-secondary);
    cursor: pointer;
    white-space: nowrap;

    a,
    a:visited {
      color: inherit;
      text-decoration: none;
    }

    .icon {
      margin-right: var(--numbers-8);
      vertical-align: text-top;
    }
  }
}
</style>
